import React, { useEffect, useRef, useState } from 'react';
import MinimalStockService from '../services/minimalStockService';
import { useTranslate } from '../translations/useTranslate';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';

const MinimalStock = ({ goTo }) => {
  const { language, t } = useTranslate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [items, setItems] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [editedItems, setEditedItems] = useState({});
  const [saving, setSaving] = useState(false);
  const [statusModal, setStatusModal] = useState(null);
  const statusModalTimerRef = useRef(null);

  // Format stock display with auto unit conversion: |v| < 1000 = g/ml, |v| >= 1000 = kg/L
  // (magnitude-based so negative stock scales the same way as positive)
  const formatStockDisplay = (value, unit) => {
    const v = Number(value || 0);
    const u = (unit || '').toString().trim();
    
    if (!u) return v.toFixed(2);
    
    const lc = u.toLowerCase();
    const isKg = lc === 'kg' || lc === 'kilogram' || lc === 'kilograms';
    const isL = lc === 'l' || lc === 'liter' || lc === 'liters' || lc === 'ltr';
    const isG = lc === 'g' || lc === 'gram' || lc === 'grams';
    const isML = lc === 'ml' || lc === 'milliliter' || lc === 'milliliters';
    
    // Helper to format number without unnecessary decimals
    const formatNum = (num) => {
      if (Number.isInteger(num)) return num.toString();
      return num.toFixed(2).replace(/\.?0+$/, '');
    };
    
    if (isKg) {
      const grams = v * 1000;
      if (Math.abs(grams) < 1000) {
        return `${formatNum(grams)} g`;
      }
      return `${formatNum(v)} kg`;
    }

    if (isL) {
      const ml = v * 1000;
      if (Math.abs(ml) < 1000) {
        return `${formatNum(ml)} ml`;
      }
      return `${formatNum(v)} L`;
    }

    if (isG) {
      if (Math.abs(v) >= 1000) {
        const kg = v / 1000;
        return `${formatNum(kg)} kg`;
      }
      return `${formatNum(v)} g`;
    }

    if (isML) {
      if (Math.abs(v) >= 1000) {
        const liters = v / 1000;
        return `${formatNum(liters)} L`;
      }
      return `${formatNum(v)} ml`;
    }
    
    return `${formatNum(v)} ${u}`;
  };

  useEffect(() => {
    return () => {
      if (statusModalTimerRef.current) {
        clearTimeout(statusModalTimerRef.current);
        statusModalTimerRef.current = null;
      }
    };
  }, []);

  const showStatusModal = (type, message, autoCloseMs = 2500) => {
    setStatusModal({ type, message });

    if (statusModalTimerRef.current) {
      clearTimeout(statusModalTimerRef.current);
      statusModalTimerRef.current = null;
    }

    if (autoCloseMs && autoCloseMs > 0) {
      statusModalTimerRef.current = setTimeout(() => {
        setStatusModal(null);
        statusModalTimerRef.current = null;
      }, autoCloseMs);
    }
  };

  const load = async () => {
    try {
      setLoading(true);
      const businessId = Number(localStorage.getItem('businessId'));
      const data = await MinimalStockService.getDashboardAlerts(businessId, language);
      if (!data?.success) throw new Error(data?.error || 'Failed to fetch');
      setItems(data.data || []);
      setEditMode(false);
      setEditedItems({});
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [language]);

  const handleEditToggle = () => {
    if (editMode) {
      // Cancel edit mode - reset changes
      setEditedItems({});
      setEditMode(false);
    } else {
      // Enter edit mode - initialize editedItems with current values
      const initialEdits = {};
      items.forEach(item => {
        initialEdits[item.item_id] = {
          reorder_point: item.reorder_point || 0,
          safety_stock: item.safety_stock || 0
        };
      });
      setEditedItems(initialEdits);
      setEditMode(true);
    }
  };

  const handleFieldChange = (itemId, field, value) => {
    setEditedItems(prev => ({
      ...prev,
      [itemId]: {
        ...prev[itemId],
        [field]: value
      }
    }));
  };

  const handleSaveChanges = async () => {
    try {
      setSaving(true);
      const businessId = Number(localStorage.getItem('businessId'));
      
      // Prepare updates array
      const updates = Object.entries(editedItems).map(([itemId, values]) => ({
        item_id: parseInt(itemId),
        reorder_point: parseFloat(values.reorder_point) || 0,
        safety_stock: parseFloat(values.safety_stock) || 0
      }));

      // Call bulk update API
      const result = await MinimalStockService.bulkUpdateReorderPoints(businessId, updates);
      
      if (!result?.success) {
        throw new Error(result?.error || 'Failed to save changes');
      }

      // Success - reload data
      showStatusModal('success', t('inventory.rop_updated_successfully') || 'Reorder points updated successfully!');
      await load();
      
    } catch (e) {
      showStatusModal('error', `${t('common.error')}: ${e.message}`);
      console.error('Error saving reorder points:', e);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {statusModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '10px'
          }}
          onClick={() => setStatusModal(null)}
        >
          <div
            style={{
              width: 'min(400px, 90vw)',
              background: '#fff',
              borderRadius: '12px',
              boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
              padding: '24px',
              textAlign: 'center'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '16px' }}>
              {statusModal.type === 'success' ? (
                <div
                  style={{
                    width: '56px',
                    height: '56px',
                    borderRadius: '50%',
                    border: '3px solid #22c55e',
                    color: '#22c55e',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '30px',
                    fontWeight: '800',
                    lineHeight: 1
                  }}
                >
                  ✓
                </div>
              ) : (
                <div
                  style={{
                    width: '56px',
                    height: '56px',
                    borderRadius: '50%',
                    border: '3px solid #ef4444',
                    color: '#ef4444',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '28px',
                    fontWeight: '800',
                    lineHeight: 1
                  }}
                >
                  !
                </div>
              )}
            </div>
            <div
              style={{
                fontSize: '18px',
                fontWeight: '600',
                marginBottom: '20px',
                color: '#1f2937',
                whiteSpace: 'pre-line'
              }}
            >
              {statusModal.message}
            </div>
            <button
              onClick={() => setStatusModal(null)}
              style={{
                padding: '10px 24px',
                backgroundColor: '#000',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              OK
            </button>
          </div>
        </div>
      )}

      <div style={{ 
      maxWidth: 600, 
      width: '100%', 
      margin: '0 auto', 
      padding: '16px',
      paddingBottom: 'calc(56px + env(safe-area-inset-bottom, 20px) + 20px)'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <button 
          onClick={() => goTo && goTo('settings')} 
          style={{ 
            border: 'none',
            background: 'transparent',
            cursor: 'pointer',
            padding: '8px',
            display: 'flex',
            alignItems: 'center',
            fontSize: '18px'
          }}
        >
          <FontAwesomeIcon icon={faArrowLeft} />
        </button>
        <h3 style={{ margin: 0 }}>{t('inventory.minimal_stock')}</h3>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          {!editMode && (
            <>
              <button 
                onClick={load} 
                style={{ padding: '6px 10px', borderRadius: 6 }}
              >
                {t('common.refresh')}
              </button>
              <button 
                onClick={() => goTo && goTo('create-reorder')} 
                style={{ 
                  padding: '6px 10px', 
                  borderRadius: 6, 
                  background: '#1976d2', 
                  color: '#fff', 
                  border: 'none' 
                }}
              >
                {t('reorder.create_reorder')}
              </button>
              <button 
                onClick={handleEditToggle} 
                style={{ 
                  padding: '6px 10px', 
                  borderRadius: 6, 
                  background: '#4caf50', 
                  color: '#fff', 
                  border: 'none' 
                }}
              >
                {t('common.edit') || 'Edit ROP'}
              </button>
            </>
          )}
          {editMode && (
            <>
              <button 
                onClick={handleEditToggle} 
                style={{ 
                  padding: '6px 10px', 
                  borderRadius: 6, 
                  border: '1px solid #ddd' 
                }}
                disabled={saving}
              >
                {t('common.cancel')}
              </button>
              <button 
                onClick={handleSaveChanges} 
                style={{ 
                  padding: '6px 10px', 
                  borderRadius: 6, 
                  background: '#4caf50', 
                  color: '#fff', 
                  border: 'none' 
                }}
                disabled={saving}
              >
                {saving ? t('common.saving') || 'Saving...' : t('common.save') || 'Save Changes'}
              </button>
            </>
          )}
        </div>
      </div>

      {loading && (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
          padding: '40px 20px',
          gap: '16px'
        }}>
          <div style={{
            width: '48px',
            height: '48px',
            border: '4px solid #f3f3f3',
            borderTop: '4px solid #3498db',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}></div>
          <p style={{
            color: '#666',
            fontSize: '16px',
            textAlign: 'center',
            margin: 0
          }}>{t('common.loading')}</p>
          <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
        </div>
      )}
      {error && <div style={{ color: 'red' }}>{error}</div>}

      {!loading && !error && (
        <div style={{ display: 'grid', gap: 8 }}>
          {items.map(it => (
            <div 
              key={it.item_id} 
              style={{ 
                border: '1px solid #eee', 
                borderRadius: 8, 
                padding: 12, 
                background: editMode ? '#f9f9f9' : '#fff' 
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ fontWeight: 700 }}>{it.name}</div>
                <span style={{ 
                  fontSize: 12, 
                  padding: '2px 8px', 
                  borderRadius: 999, 
                  background: it.urgency_level === 'critical' ? '#fce4ec' : '#fff8e1', 
                  color: it.urgency_level === 'critical' ? '#c2185b' : '#ef6c00' 
                }}>
                  {it.urgency_level === 'critical' ? t('inventory.critical') : t('inventory.low')}
                </span>
              </div>
              
              {!editMode && (
                <div style={{ marginTop: 6, fontSize: 13, color: '#555' }}>
                  {t('inventory.current')}: {formatStockDisplay(it.current_stock, it.unit)} | 
                  {' '}{t('inventory.rop')}: {formatStockDisplay(it.reorder_point || 0, it.unit)} | 
                  {' '}{t('inventory.safety')}: {formatStockDisplay(it.safety_stock || 0, it.unit)}
                </div>
              )}

              {editMode && (
                <div style={{ marginTop: 12 }}>
                  {/* Current Stock - Read Only Display */}
                  <div style={{ marginBottom: 12, padding: 8, background: '#f5f5f5', borderRadius: 4 }}>
                    <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>
                      {t('inventory.current')}
                    </div>
                    <div style={{ fontSize: 14, color: '#333', fontWeight: 500 }}>
                      {formatStockDisplay(it.current_stock, it.unit)}
                    </div>
                  </div>
                  
                  {/* Editable Fields - Only ROP and Safety */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <div>
                      <label style={{ display: 'block', fontSize: 12, color: '#666', marginBottom: 4 }}>
                        {t('inventory.rop')} *
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={editedItems[it.item_id]?.reorder_point || 0}
                        onChange={(e) => handleFieldChange(it.item_id, 'reorder_point', e.target.value)}
                        style={{ 
                          width: '100%', 
                          padding: '8px 10px', 
                          border: '2px solid #4caf50', 
                          borderRadius: 4,
                          fontSize: 14,
                          background: '#fff'
                        }}
                      />
                    </div>
                    
                    <div>
                      <label style={{ display: 'block', fontSize: 12, color: '#666', marginBottom: 4 }}>
                        {t('inventory.safety')} *
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={editedItems[it.item_id]?.safety_stock || 0}
                        onChange={(e) => handleFieldChange(it.item_id, 'safety_stock', e.target.value)}
                        style={{ 
                          width: '100%', 
                          padding: '8px 10px', 
                          border: '2px solid #4caf50', 
                          borderRadius: 4,
                          fontSize: 14,
                          background: '#fff'
                        }}
                      />
                    </div>
                  </div>
                </div>
              )}

              {!!it.vendor_name && (
                <div style={{ marginTop: 6, fontSize: 12, color: '#666' }}>
                  {t('vendors.vendor')}: {it.vendor_name}
                </div>
              )}
            </div>
          ))}
          {items.length === 0 && (
            <div style={{ padding: 12, color: '#666' }}>
              {t('inventory.no_low_stock_items')}
            </div>
          )}
        </div>
      )}
      </div>
    </>
  );
};

export default MinimalStock;
